import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { label: "Startseite", path: "/" },
  {
    label: "Unternehmen",
    children: [
      { label: "Bauunternehmung", path: "/bauunternehmung", color: "blue" },
      { label: "Galabau", path: "/galabau", color: "green" },
      { label: "Sachverständiger", path: "/sachverstaendiger", color: "orange" },
    ],
  },
];

export const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();

  const colorMap = {
    blue: "text-brand-blue-light",
    green: "text-brand-green-light",
    orange: "text-brand-orange-light",
  };

  const dotMap = {
    blue: "bg-brand-blue",
    green: "bg-brand-green",
    orange: "bg-brand-orange",
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="container-wide flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 group">
          <div className="flex gap-0.5">
            <div className="w-2 h-8 gradient-blue" />
            <div className="w-2 h-8 gradient-orange" />
            <div className="w-2 h-8 gradient-green" />
          </div>
          <span className="font-heading font-bold text-lg md:text-xl tracking-tight text-foreground">
            FREITAG<span className="text-muted-foreground font-normal">-GRUPPE</span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.children ? (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => setDropdownOpen(true)}
                onMouseLeave={() => setDropdownOpen(false)}
              >
                <button className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground font-body uppercase tracking-wider">
                  {link.label}
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-card border border-border shadow-lg"
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className="flex items-center gap-3 px-4 py-3 text-sm font-body hover:bg-secondary"
                        >
                          <span className={`w-2 h-2 ${dotMap[child.color]}`} />
                          <span className={colorMap[child.color]}>{child.label}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium font-body uppercase tracking-wider ${
                  location.pathname === link.path
                    ? "text-foreground"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <Button variant="blue" size="sm" asChild>
            <Link to="/#kontakt">Kontakt</Link>
          </Button>
        </div>

        {/* Mobile Toggle */}
        <button
          className="md:hidden text-foreground p-2"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-card border-b border-border overflow-hidden"
          >
            <div className="px-6 py-6 space-y-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="block text-sm font-medium font-body uppercase tracking-wider text-foreground"
              >
                Startseite
              </Link>
              <div className="space-y-2 pl-4 border-l border-border">
                {navLinks[1].children.map((child) => (
                  <Link
                    key={child.path}
                    to={child.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 text-sm font-body ${colorMap[child.color]}`}
                  >
                    <span className={`w-2 h-2 ${dotMap[child.color]}`} />
                    {child.label}
                  </Link>
                ))}
              </div>
              <Button variant="blue" size="sm" className="w-full" asChild>
                <Link to="/#kontakt" onClick={() => setMobileOpen(false)}>Kontakt</Link>
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
